import React, {
  useCallback,
  useRef,
  useState,
} from 'react';
import IconFont from '$/components/IconFont';
import { message } from '$/components/Message';
import { useLanguageStore } from '$/store/languageStore';
import { getEnv, navigate } from '@/common/agents';
import { leaveTeam, teamSwitch } from '@/domains/team/teamService';
import { clearTeamInfo } from '@/store/useTeamStore';
import LeaveTeamModal from '@/components/TeamManagement/LeaveTeamModal';
import styles from './index.module.scss';

/** 从团队操作异常的常见结构中提取服务端提示。 */
function getTeamAccessActionErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  if (!error || typeof error !== 'object') return fallback;
  const errorRecord = error as {
    message?: unknown;
    data?: { message?: unknown; };
    response?: { data?: { message?: unknown; }; };
  };
  const errorMessage = errorRecord.data?.message
    ?? errorRecord.response?.data?.message
    ?? errorRecord.message;
  return typeof errorMessage === 'string' && errorMessage ? errorMessage : fallback;
}

/** 清理团队上下文并回到当前环境的个人空间首页。 */
function navigateToPersonalHome(): void {
  clearTeamInfo();
  navigate({
    mode: 'url',
    url: getEnv().ENV === 'development' ? '/--roboneo/home' : '/home',
    replace: true,
  });
}

/**
 * 团队首页接口返回席位冻结业务码时覆盖当前页面，并提供统一的身份退出操作。
 */
export default function TeamAccessSuspendedOverlay() {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const leaveTeamText = languageStore.smartcatTranslationText((value) => value.leave_the_team);
  const suspendedTitleText = languageStore.smartcatTranslationText(
    (value) => value.insufficient_team_seats_your_team_permissions_are_temporarily_paused,
  );
  const suspendedDescriptionText = languageStore.smartcatTranslationText(
    (value) => value.you_will_be_automatically_restored_in_join_order_after_the_team_admin_adds_seats_or_releases_existing_ones_during_the_freeze_period_you_can_still_normally_use_your_personal_space,
  );
  const returnPersonalSpaceText = languageStore.smartcatTranslationText(
    (value) => value.return_to_personal_space,
  );
  const switchPersonalSpaceFailedText = languageStore.smartcatTranslationText(
    (value) => value.switch_to_personal_space_failed,
  );
  const leaveTeamFailedText = languageStore.smartcatTranslationText(
    (value) => value.leave_team_failed,
  );
  const actionPendingRef = useRef(false);
  const [pending, setPending] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);

  /** 退出团队入口仅打开挽留弹窗，用户确认前不执行退出请求。 */
  const handleOpenLeaveModal = useCallback(() => {
    if (!actionPendingRef.current) setLeaveModalOpen(true);
  }, []);

  /** 关闭挽留弹窗后继续停留在当前团队首页和权限暂停浮层。 */
  const handleCloseLeaveModal = useCallback(() => {
    if (!actionPendingRef.current) setLeaveModalOpen(false);
  }, []);

  /** 席位冻结成员主动切换回个人身份，并返回个人空间首页。 */
  const handleReturnPersonalSpace = useCallback(async () => {
    if (actionPendingRef.current) return;
    actionPendingRef.current = true;
    setPending(true);
    try {
      await teamSwitch(0);
      navigateToPersonalHome();
    } catch (error) {
      message.error(getTeamAccessActionErrorMessage(error, switchPersonalSpaceFailedText));
      actionPendingRef.current = false;
      setPending(false);
    }
  }, [switchPersonalSpaceFailedText]);

  /** 席位冻结成员退出当前团队后，清理失效团队上下文并返回个人空间。 */
  const handleLeaveCurrentTeam = useCallback(async () => {
    if (actionPendingRef.current) return;
    actionPendingRef.current = true;
    setPending(true);
    try {
      await leaveTeam();
      try {
        await teamSwitch(0);
      } catch (switchError) {
        console.error('switch personal space after leaving team error:', switchError);
      }
      navigateToPersonalHome();
    } catch (error) {
      message.error(getTeamAccessActionErrorMessage(error, leaveTeamFailedText));
      actionPendingRef.current = false;
      setPending(false);
    }
  }, [leaveTeamFailedText]);

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="team-access-suspended-title"
      aria-describedby="team-access-suspended-description"
    >
      <div className={styles.content}>
        <div className={styles.info}>
          <IconFont
            name="lockBold"
            fontSize={64}
            className={styles.icon}
          />
          <div className={styles.text}>
            <h1 id="team-access-suspended-title" className={styles.title}>
              {suspendedTitleText}
            </h1>
            <p id="team-access-suspended-description" className={styles.description}>
              {suspendedDescriptionText}
            </p>
          </div>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.secondaryButton}
            disabled={pending}
            onClick={handleOpenLeaveModal}
          >
            {leaveTeamText}
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            disabled={pending}
            onClick={handleReturnPersonalSpace}
          >
            {returnPersonalSpaceText}
          </button>
        </div>
      </div>
      <LeaveTeamModal
        variant={leaveModalOpen ? 'confirm' : null}
        isPending={pending}
        onClose={handleCloseLeaveModal}
        onConfirm={handleLeaveCurrentTeam}
        onGoToMemberManagement={handleCloseLeaveModal}
      />
    </div>
  );
}
